import EmailService from './EmailService';
import StaffService from './StaffService';
import { formatDate } from '../utils/dateFormat';

const STORAGE_KEY = 'safeguardpro_reminder_log';
const THROTTLE_HOURS = 24;

const readLog = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const writeLog = (entries) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
};

const findLatestEntry = (entries, entityId, staffId) =>
  entries
    .filter((entry) => entry.entityId === entityId && entry.staffId === staffId)
    .sort((a, b) => new Date(b.sentAt) - new Date(a.sentAt))[0];

export default class ReminderService {
  constructor() {
    this.emailService = new EmailService();
    this.staffService = new StaffService();
  }

  list() {
    return readLog();
  }

  getLastReminder(entityId, staffId) {
    const entries = readLog();
    return findLatestEntry(entries, entityId, staffId) || null;
  }

  isThrottled(entityId, staffId) {
    const latest = this.getLastReminder(entityId, staffId);
    if (!latest) return false;
    const diffHours = (Date.now() - new Date(latest.sentAt).getTime()) / (1000 * 60 * 60);
    return diffHours < THROTTLE_HOURS;
  }

  recordReminder(entry) {
    const entries = readLog();
    const next = [entry, ...entries].slice(0, 500);
    writeLog(next);
    return entry;
  }

  sendInventoryReminder({ item, assignment, staffId, forced = false }) {
    const staff = this.staffService.getById(staffId);
    if (!staff?.loginEmail) {
      return { status: 'failed', error: 'No staff email configured.' };
    }
    const entityId = `${item.id}:${assignment.id}`;
    if (!forced && this.isThrottled(entityId, staffId)) {
      return { status: 'throttled', error: 'Reminder was already sent within 24 hours.' };
    }

    const actionUrl = typeof window !== 'undefined'
      ? `${window.location.origin}/inventory/${item.id}`
      : `/inventory/${item.id}`;

    const logEntry = this.emailService.sendTemplate({
      templateKey: 'reminder.overdue',
      to: staff.loginEmail,
      data: {
        staff_name: staff.name,
        item_title: `${item.name} (${item.reference})`,
        due_date: assignment.dueDate ? formatDate(assignment.dueDate) : 'N/A',
        action_url: actionUrl,
        site_name: 'SafeguardPro'
      },
      entity: { type: 'inventory', id: item.id }
    });

    const reminderEntry = {
      id: `rem-${Math.random().toString(36).slice(2, 8)}`,
      entityId,
      staffId,
      itemId: item.id,
      assignmentId: assignment.id,
      sentAt: new Date().toISOString(),
      status: logEntry.status,
      emailLogId: logEntry.id
    };
    this.recordReminder(reminderEntry);
    return { status: logEntry.status, log: reminderEntry };
  }

  runScheduledInventoryReminders({ inventoryItems, inventoryController }) {
    const staffList = this.staffService.list();
    const staffByName = new Map(staffList.map((staff) => [staff.name, staff]));
    const overdueItems = inventoryItems.filter((item) => {
      const dueStatus = inventoryController.getDueStatus(item);
      return dueStatus.isOverdue;
    });

    overdueItems.forEach((item) => {
      const assignment = inventoryController.getActiveAssignment(item);
      if (!assignment) return;
      const staff = staffByName.get(assignment.assignedToName || '') || staffList.find((entry) => entry.id === assignment.assignedToId);
      if (!staff) return;
      if (!staff.loginEmail) return;
      this.sendInventoryReminder({ item, assignment, staffId: staff.id });
    });
  }
}
